import {useCallback, useEffect, useState} from 'react';
import {authHeader} from '../services/auth-header';

const baseUrl = () => new URL(process.env.REACT_APP_API_URL || '/api/', window.location.origin);
export const privateMediaUrl = (source) => {
    if (typeof source !== 'string') return null;
    const base = baseUrl();
    if (source.startsWith('/api/media/')) return new URL(`media/${source.slice('/api/media/'.length)}`, base).href;
    try {
        const candidate = new URL(source, window.location.origin);
        return candidate.origin === base.origin && candidate.pathname.startsWith(`${base.pathname}media/`)
            ? candidate.href : null;
    } catch (_) { return null; }
};
export const fetchPrivateMedia = async (source, options = {}) => {
    const url = privateMediaUrl(source);
    if (!url) throw new Error('Neplatný odkaz na súbor.');
    const response = await fetch(url, {...options, headers: {...authHeader(), ...options.headers}, cache: 'no-store'});
    if (!response.ok) throw new Error(response.status === 401 ? 'Prihláste sa znova.' : 'Súbor sa nepodarilo načítať.');
    return response;
};
export const usePrivateImage = (source, {enabled = true, loadBlob} = {}) => {
    const [loaded, setLoaded] = useState({source: null, url: undefined, error: false});
    const [revision, setRevision] = useState(0);
    const retry = useCallback(() => {
        setLoaded({source: null, url: undefined, error: false});
        setRevision((current) => current + 1);
    }, []);
    const privateUrl = privateMediaUrl(source);
    useEffect(() => {
        if (!privateUrl || !enabled) {
            setLoaded(current => current.source === null ? current : {source: null, url: undefined, error: false});
            return undefined;
        }
        const controller = new AbortController();
        let objectUrl;
        let active = true;
        const request = loadBlob ? loadBlob(source, controller.signal)
            : fetchPrivateMedia(source, {signal: controller.signal}).then(response => response.blob());
        request.then((blob) => {
            if (!active) return;
            objectUrl = URL.createObjectURL(blob);
            setLoaded({source, url: objectUrl, error: false});
        }).catch((error) => {
            if (active && error.name !== 'AbortError') setLoaded({source, url: undefined, error: true});
        });
        return () => {
            active = false;
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [source, privateUrl, revision, enabled, loadBlob]);
    return {src: enabled ? privateUrl ? (loaded.source === source ? loaded.url : undefined) : source : undefined,
        loading: Boolean(enabled && privateUrl && (loaded.source !== source || (!loaded.url && !loaded.error))),
        error: Boolean(enabled && privateUrl && loaded.source === source && loaded.error), retry};
};
export const mediaExtension = (response) => ({'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif',
    'image/svg+xml': 'svg'}[(response.headers.get('content-type') || '').split(';')[0]] || 'bin');
export const downloadPrivateMedia = async (source) => {
    const response = await fetchPrivateMedia(source);
    const blob = await response.blob();
    const {saveAs} = await import('file-saver');
    saveAs(blob, `subor.${mediaExtension(response)}`);
};
